import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { Loader2, Minus, Plus } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDebounced } from "@/lib/useDebounced";

import {
  DEFAULT_BASIS, stagePrice, unstagePrice, type Basis, type GridRow, type Market, type PriceRunLine, type PriceRuns, type StageBody, type UpcomingFee,
} from "./api";
import {
  ABSENT, BASIS_LABEL, belowTarget, COMPONENT_LABEL, COPY, countryName, grossTone, humanDate, MARKET_LABEL, money, orders, pct1, priceInForce, projectionMissing,
  projectionWeighting, signedPct, STORE_LABEL, vatPct, type MarginTarget,
} from "./format";
import { cleanPrice, savedLine, stageRefusal, stageTarget, stepPrice, type Refusal } from "./price";
import { projectionBody, projectionQuery, projectionRefusal, type ProjectedComponent, type Projection } from "./projection";
import { offersOverride, overrideFields, OVERRIDE_COPY } from "./overrides";
import { exceptionFields, RRP_COPY, storeToday, type RrpLookup } from "./rrp";
import { EVAL_COPY, SOURCE_WORD, STATUS_WORD, TONE_CLASS, type StrategyBlock } from "./strategies";
import { floorInCurrency, type FloorRow } from "./config";
import { ORDER_TYPE_COPY, saveViewLine, type ViewLookup } from "./order_views";
import { AS_OF_COPY, changeText } from "./as_of";
import { BasketScenarios } from "./BasketScenarios";
import { invalidateBatch } from "./batch";
import { mergeIssues } from "./feed_issues";
import { FeedIssuesNote } from "./FeedIssuesNote";

/**
 * The New price box at the top of the row detail (UX subset R4, approved mockup v1). The floor is named from the Config
 * read (C1 v1.16, `floor`: the row, null when none is set, undefined while unread) and judged only by C2: a save below the
 * floor answers 409 with the floor, the reason field opens, and the next save carries the reason. The RRP exception works the same way (C1 v1.6, M25 `exceptionFields`): a save below the
 * region RRP on a gated row answers 409 `below_rrp` (or `rrp_not_comparable`), the reason and until fields open, and the
 * next save carries both; the browser never compares the typed price with the RRP. A view-only person sees the same box;
 * the shell renders its controls inert and C2 refuses the write (H-3), which the result line then says.
 * The margins at the typed price (C1 v1.11, closes v1.1 debt D1) are C2's projection (`POST /api/price-run/project`),
 * asked once the typed price has rested and shown beside the committed price's figures through the same arithmetic;
 * `basis` is the grid's COGS basis on view, `target` the Config margin target for the Below target tone.
 * `view` is the order type on screen (C1 v1.23): in the Subscription view the list price being set is shown beside the net
 * it implies (C2's, at the saved or committed list price); in a partner view Save is off, since partner prices are set in
 * Discounts (C2 v1.23 ruling A5), while Remove stays (it removes a saved list price, not a partner price); in either, the
 * projection is labelled as the typed list price's, apart from the view block above it. Absent, Single order: the box as before.
 * `asOf` is the picked date (C1 v1.24): the projection is C2's with the fees in force on it (C2 v1.26), and names the fees that
 * differ from today's; the price saved is still the one that goes live at commit. Absent or null, today: the box as before.
 * `feeChange` is the row's next fee change when C2 serves its date (C1 v1.28): on today's figures the box also asks C2 for
 * the margins at the typed price on that date and shows them under today's. Absent, null or on a picked date: nothing more.
 * C1 v1.50 (c1-overrides, CL-040): on an Amazon marketplace row the box offers Temporary price, a reason and an until date saved with the
 * price (C2 v1.52's pair); the saved line carries them, the commit records them, and the morning after the date the return is written.
 * A store row offers nothing (C2 refuses it: a store promotion is a discount). Both fields or neither; the browser judges no date.
 * `carry` is a grid cell's save that needs fields the cell cannot hold (C1 v1.57, GATE-1084 PS-2 A): the box opens with the typed
 * price, C2's refusal and the fields it asks for, so the person finishes here. Absent: the box as before.
 */
export function NewPriceBox({ row, market, runs, areaName, rrp, basis = DEFAULT_BASIS, target, floor, view, asOf = null, feeChange = null, carry }: {
  row: GridRow; market: Market; runs: PriceRuns | undefined; areaName: string; rrp?: RrpLookup; basis?: Basis; target?: MarginTarget;
  floor?: FloorRow | null; view?: { lookup: ViewLookup; partner: boolean; label: string }; asOf?: string | null; feeChange?: UpcomingFee | null;
  carry?: { value: string; refusal: Refusal | null };
}) {
  const client = useQueryClient();
  const saved = savedLine(runs, row, market);
  const currency = row.price?.currency ?? row.currency ?? saved?.currency ?? null;
  const [value, setValue] = useState(carry?.value ?? saved?.new_price ?? row.price?.value ?? "");
  const [reason, setReason] = useState(saved?.floor_override_reason ?? "");
  const [needsReason, setNeedsReason] = useState(Boolean(saved?.floor_override_reason) || Boolean(carry?.refusal?.needsReason));
  const [exReason, setExReason] = useState(saved?.rrp_exception_reason ?? "");
  const [exUntil, setExUntil] = useState(saved?.rrp_exception_until ?? "");
  const [needsException, setNeedsException] = useState(Boolean(saved?.rrp_exception_reason) || Boolean(carry?.refusal?.needsException));
  const [ovOpen, setOvOpen] = useState(Boolean(saved?.override_until));
  const [ovReason, setOvReason] = useState(saved?.override_reason ?? "");
  const [ovUntil, setOvUntil] = useState(saved?.override_until ?? "");
  const [result, setResult] = useState<{ tone: "ok" | "err"; text: string } | null>(carry?.refusal ? { tone: "err", text: carry.refusal.text } : null);
  // A floor in another currency than the price cannot be compared, so C2 asks a reason for every save (`floor_not_comparable`):
  // the reason field is open from the start rather than after a certain refusal (C1 v1.16, owner ruling R2-A).
  const otherCurrency = floor != null && !floorInCurrency(floor, currency);
  const reasonOpen = needsReason || otherCurrency;
  // The saved line is the truth for what is in the batch. When it arrives after the sheet opened, or changes because a
  // save landed on another tab, the box follows it; the result line is left alone so a just-saved message stays.
  const savedId = saved?.id ?? null;
  const savedPrice = saved?.new_price ?? null;
  const savedReason = saved?.floor_override_reason ?? null;
  const savedExReason = saved?.rrp_exception_reason ?? null;
  const savedExUntil = saved?.rrp_exception_until ?? null;
  const savedOvReason = saved?.override_reason ?? null;
  const savedOvUntil = saved?.override_until ?? null;
  // A carried price is the person's latest word, so the saved line does not replace it when the box opens.
  const carried = useRef(Boolean(carry));
  useEffect(() => {
    if (carried.current) { carried.current = false; return; }
    if (savedId === null) return;
    setValue(savedPrice ?? "");
    setReason(savedReason ?? "");
    setNeedsReason(Boolean(savedReason));
    setExReason(savedExReason ?? "");
    setExUntil(savedExUntil ?? "");
    setNeedsException(Boolean(savedExReason));
    setOvReason(savedOvReason ?? "");
    setOvUntil(savedOvUntil ?? "");
    setOvOpen(Boolean(savedOvUntil));
  }, [savedId, savedPrice, savedReason, savedExReason, savedExUntil, savedOvReason, savedOvUntil]);
  // A save or a remove drops the reads of the batch (`invalidateBatch`, C1 v1.41): the runs, the strategy evaluation, the Customer
  // lens and the KPI reads; the commit review's Remove drops the same.
  const refresh = () => invalidateBatch(client);
  const today = storeToday(rrp);
  // The projection follows the typed price after it has rested (one read per pause, never per keystroke); a value that is
  // not a price asks nothing and the block says so. The answer is C2's; nothing here computes a margin or a fee.
  const typed = cleanPrice(useDebounced(value, 400));
  const projection = useQuery({ ...projectionQuery(projectionBody(row, typed ?? "", basis, asOf)), enabled: typed !== null });
  // The same price on the date of the row's next fee change (C1 v1.28): C2's answer, a second read per pause, today's figures only.
  const feeDay = asOf === null && feeChange ? feeChange.effective_from : null;
  const feeDayProjection = useQuery({ ...projectionQuery(projectionBody(row, typed ?? "", basis, feeDay)), enabled: typed !== null && feeDay !== null });

  type Body = Omit<StageBody, "xero_sku" | "marketplace_id">;
  const stage = useMutation({
    mutationFn: (body: Body) => stagePrice({ ...stageTarget(row), ...body }),
    onSuccess: (line: PriceRunLine) => {
      refresh();
      setNeedsException(Boolean(line.rrp_exception_reason));
      const recorded = line.rrp_exception_reason && line.rrp_exception_until ? RRP_COPY.box.recorded(humanDate(line.rrp_exception_until)) : "";
      // C1 v1.50: the temporary price's until date and the price it returns to (the line's `old_price`, C2's), as C2 saved them.
      const temporary = line.override_until ? OVERRIDE_COPY.box.recorded(humanDate(line.override_until), line.old_price == null ? null : money(line.old_price, line.currency)) : "";
      setOvOpen(Boolean(line.override_until));
      setResult({ tone: "ok", text: COPY.saved(money(line.new_price, line.currency), Boolean(line.floor_override_reason)) + recorded + temporary });
    },
    onError: (error, body) => {
      const r = stageRefusal(error, areaName, currency, money(body.new_price, currency), today);
      if (r.needsReason) setNeedsReason(true);
      if (r.needsException) setNeedsException(true);
      setResult({ tone: "err", text: r.text });
    },
  });
  const remove = useMutation({
    mutationFn: (lineId: number) => unstagePrice(lineId),
    onSuccess: () => {
      refresh(); setNeedsReason(false); setReason(""); setNeedsException(false); setExReason(""); setExUntil(""); setOvOpen(false); setOvReason(""); setOvUntil("");
      setResult({ tone: "ok", text: COPY.removed });
    },
    onError: (error) => setResult({ tone: "err", text: stageRefusal(error, areaName, currency, "").text }),
  });
  const submit = () => {
    const price = cleanPrice(value);
    if (!price) { setResult({ tone: "err", text: COPY.enterPrice }); return; }
    const body: Body = { new_price: price };
    const trimmed = reason.trim();
    if (trimmed) body.floor_override_reason = trimmed;
    const exception = exceptionFields(exReason, exUntil);
    if (!exception) { setResult({ tone: "err", text: RRP_COPY.box.both }); return; }
    // C1 v1.50: with Temporary price ticked both fields are needed (C2 refuses half a pair); unticked, the pair is not sent and a re-save clears it.
    const temporary = ovOpen ? overrideFields(ovReason, ovUntil) : {};
    if (!temporary || (ovOpen && !temporary.override_until)) { setResult({ tone: "err", text: OVERRIDE_COPY.box.both }); return; }
    stage.mutate({ ...body, ...exception, ...temporary });
  };

  const place = row.channel === "amazon"
    ? (row.marketplace_name ?? row.marketplace_id)
    : `${STORE_LABEL[row.channel_location] ?? `Shopify ${MARKET_LABEL[market]}`} (${COPY.onePrice})`;
  const busy = stage.isPending || remove.isPending;
  const rrpRow = rrp?.kind === "row" && rrp.row.rrp != null ? rrp.row : null;
  const label = reasonOpen && !reason.trim() ? COPY.saveOverride : needsException ? RRP_COPY.box.saveException : ovOpen ? OVERRIDE_COPY.box.save : COPY.save;
  const canOverride = offersOverride(row);
  const viewLine = view && !view.partner ? saveViewLine(view.lookup) : null;
  const inForce = priceInForce(row.price, row.price ? money(row.price.value, row.price.currency) : null);
  return (
    <section aria-labelledby="pp-new-price" className="rounded-md border border-ks-grey-2 p-3">
      <div id="pp-new-price" className="text-xs font-semibold uppercase tracking-wide text-ks-purple">{COPY.newPrice} · {place}</div>
      <p className="text-xs text-ks-grey-1">{inForce.line} · {floor ? (floorInCurrency(floor, currency) ? COPY.floorIs(money(floor.floor, floor.currency)) : COPY.floorOther(money(floor.floor, floor.currency), floor.currency ?? "", currency ?? "")) : floor === null ? COPY.noFloor : COPY.floorNote}</p>
      {rrpRow && (
        <p className="text-xs text-ks-grey-1">
          {RRP_COPY.box.line(money(rrpRow.rrp, rrpRow.currency), rrpRow.gated ? RRP_COPY.gated : rrpRow.is_anchor ? RRP_COPY.anchorNotGated : RRP_COPY.notGated)}
        </p>
      )}
      {viewLine && <p className="text-xs text-ks-ink">{viewLine}</p>}
      {view?.partner && <p role="status" className="text-xs text-ks-ink">{ORDER_TYPE_COPY.partnerBanner(view.label)}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" aria-label={COPY.lower} disabled={busy} onClick={() => setValue(stepPrice(value, -1, currency))}><Minus aria-hidden className="size-4" /></Button>
        <Label htmlFor="pp-new-price-input" className="sr-only">{COPY.newPrice}</Label>
        <Input id="pp-new-price-input" inputMode="decimal" className="w-28 tabular-nums" value={value} onChange={(e) => setValue(e.target.value)} />
        <Button type="button" size="sm" variant="outline" aria-label={COPY.raise} disabled={busy} onClick={() => setValue(stepPrice(value, 1, currency))}><Plus aria-hidden className="size-4" /></Button>
        {currency && <span className="text-sm text-ks-grey-1">{currency}</span>}
      </div>
      {view && <p className="mt-1 text-xs text-ks-grey-1">{ORDER_TYPE_COPY.boxAtList}</p>}
      <ProjectionBlock current={inForce.word} typed={typed} currency={currency} query={projection} target={target} own={row.grain === "ship_country" ? row.location : null} asOf={asOf} onUse={(price) => { setValue(price); setResult(null); }} />
      {feeDay !== null && projection.data && !projection.isError && <FeeDayBlock current={inForce.word} day={feeDay} today={projection.data} query={feeDayProjection} />}
      {/* C1 v1.40 (c1-scenarios, CL-023): a Shopify row's baskets at the same rested price, so an edit re-finds the worst one. */}
      {row.channel === "shopify" && <BasketScenarios row={row} price={typed} basis={basis} />}
      {reasonOpen && (
        <div className="mt-2 flex flex-col gap-1">
          <Label htmlFor="pp-override">{otherCurrency ? COPY.reasonLabelOther : COPY.reasonLabel}</Label>
          <Input id="pp-override" value={reason} placeholder={otherCurrency ? COPY.reasonPlaceholderOther : COPY.reasonPlaceholder} onChange={(e) => setReason(e.target.value)} />
        </div>
      )}
      {needsException && (
        <div className="mt-2 flex flex-col gap-1 rounded-md border border-ks-warn p-2">
          <Badge variant="outline" className="w-fit border-ks-warn text-ks-warn">{RRP_COPY.parityRisk}</Badge>
          <Label htmlFor="pp-rrp-reason">{RRP_COPY.box.reasonLabel}</Label>
          <Input id="pp-rrp-reason" value={exReason} maxLength={500} placeholder={RRP_COPY.box.reasonPlaceholder} onChange={(e) => setExReason(e.target.value)} />
          <Label htmlFor="pp-rrp-until">{RRP_COPY.box.untilLabel}</Label>
          <Input id="pp-rrp-until" type="date" className="w-44" min={today} value={exUntil} onChange={(e) => setExUntil(e.target.value)} />
          <p className="text-xs text-ks-grey-1">{RRP_COPY.box.note}</p>
        </div>
      )}
      {canOverride && (
        <div className="mt-2 flex flex-col gap-1" data-override-box>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={ovOpen} disabled={busy} onCheckedChange={(c) => { setOvOpen(c === true); if (c !== true) { setOvReason(""); setOvUntil(""); } }} />
            {OVERRIDE_COPY.box.toggle}
          </label>
          {ovOpen && (
            <div className="flex flex-col gap-1 rounded-md border border-ks-purple p-2">
              <p className="text-xs text-ks-grey-1">{OVERRIDE_COPY.box.toggleNote}</p>
              <Label htmlFor="pp-ov-reason">{OVERRIDE_COPY.box.reasonLabel}</Label>
              <Input id="pp-ov-reason" value={ovReason} maxLength={500} placeholder={OVERRIDE_COPY.box.reasonPlaceholder} onChange={(e) => setOvReason(e.target.value)} />
              <Label htmlFor="pp-ov-until">{OVERRIDE_COPY.box.untilLabel}</Label>
              <Input id="pp-ov-until" type="date" className="w-44" min={today} value={ovUntil} onChange={(e) => setOvUntil(e.target.value)} />
              <p className="text-xs text-ks-grey-1">{OVERRIDE_COPY.box.note}</p>
            </div>
          )}
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <Button type="button" size="sm" disabled={busy || Boolean(view?.partner)} aria-busy={stage.isPending} onClick={submit}>
          {stage.isPending ? <><Loader2 aria-hidden className="size-4 animate-spin" />{COPY.saving}</> : label}
        </Button>
        {saved && (
          <>
            <Badge variant="outline" className="border-ks-warn text-ks-warn">{COPY.savedChip(money(saved.new_price, saved.currency))}</Badge>
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => remove.mutate(saved.id)}>{COPY.remove}</Button>
          </>
        )}
      </div>
      {/* AGENTS.md rule 14: a refusal is an alert (inverted portal-wide); a success stays a status line. */}
      {result && <p role={result.tone === "err" ? "alert" : "status"} className={result.tone === "err" ? "mt-2 text-sm" : "mt-2 text-sm text-ks-pass"}>{result.text}</p>}
    </section>
  );
}

/** One fee at the candidate price: the figure, and how it was reached (recomputed from the registry at its rate, or fixed as fed). */
function componentText(c: ProjectedComponent, currency: string | null): string {
  const figure = c.value == null ? COPY.projNoCost : money(c.value, c.currency ?? currency);
  if (c.method === "fixed") return `${COMPONENT_LABEL[c.component]} ${figure} (${COPY.projFixed})`;
  const how = c.component === "referral" ? COPY.projRecomputedPrice(vatPct(c.rate)) : COPY.projRecomputedFees(vatPct(c.rate));
  const minimum = c.minimum_applied && c.minimum != null ? `, ${COPY.projMinimum(money(c.minimum, c.currency ?? currency))}` : "";
  const standard = c.standard_value != null ? `, ${COPY.projStandard(money(c.standard_value, c.currency ?? currency))}` : "";
  return `${COMPONENT_LABEL[c.component]} ${figure} (${how}${minimum}${standard})`;
}

/** The three comparisons C2 states (brief D6): against the price in force, the Amazon UK price and the RRP, each with its
 * reason when it cannot be made. The first is named as the price in force is (`current`: "vs committed" or "vs live price").
 * Every figure is C2's string with its sign. The Amazon UK row is its own anchor (C2 D5): it says so instead of comparing with
 * itself. */
function metricsText(p: Projection, current: string): string {
  const { vs_previous_price_pct: prev, vs_amazon_uk_pct: uk, vs_rrp_pct: rrp } = p.metrics;
  const vs = COPY.projVs(current);
  const previous = prev.value == null ? `${vs} ${ABSENT} (${prev.reason ?? COPY.projNoCommitted})` : `${vs} ${signedPct(prev.value)}`;
  const converted = uk.fx_rate != null ? ` (${COPY.projConverted(money(uk.compared_price, uk.anchor_currency), humanDate(uk.fx_rate_date))})` : "";
  const amazonUk = uk.anchor_sku === p.xero_sku
    ? `${COPY.projVsAmazonUk} ${ABSENT} (${COPY.projAnchorRow})`
    : uk.value == null ? `${COPY.projVsAmazonUk} ${ABSENT} (${uk.reason ?? ABSENT})` : `${COPY.projVsAmazonUk} ${signedPct(uk.value)}${converted}`;
  const rrpNote = rrp.value == null ? ` (${rrp.reason ?? ABSENT})` : ` (${rrp.below ? COPY.projBelowRrp(money(rrp.rrp, rrp.rrp_currency)) : COPY.projAtOrAboveRrp(money(rrp.rrp, rrp.rrp_currency))})`;
  return `${previous} · ${amazonUk} · ${COPY.projVsRrp} ${signedPct(rrp.value)}${rrpNote}`;
}

/**
 * The margins at the typed price, under the stepper (UX subset R4: "At {price}: Gross X · Fulfil Y"). Not a price typed:
 * one sentence. The read pending: a status line. Refused or failed: an alert (AGENTS.md rule 14). Answered: the figures at
 * the candidate price beside the live price's, the fee parts (Amazon: commission and DSF recomputed at the registry
 * rate, the FBA fee fixed; Shopify: one line per ship country, every part fixed, the store figure sales-weighted), the three
 * comparisons, every missing input named, and the basis and net price the figures rest on. `asOf`: on a picked date the
 * figures name C2's date (C1 v1.24); the fees that differ from today's on it and that the rules are judged with them are said
 * only when C2 answered the date asked. C2 v1.27 answers a date on or before its own today with today's fees and names today
 * (a grid two or more days old offers such a date), and then there is nothing to compare (D7, C1 v1.25).
 */
function ProjectionBlock({ current, typed, currency, query, target, own, asOf, onUse }: {
  current: string; typed: string | null; currency: string | null; query: UseQueryResult<Projection>; target: MarginTarget; own: string | null; asOf: string | null; onUse: (price: string) => void;
}) {
  if (typed === null) return <p className="mt-1 text-xs text-ks-grey-1">{COPY.projEnter}</p>;
  if (query.isError) return <p role="alert" className="mt-1 text-sm">{projectionRefusal(query.error)}</p>;
  if (!query.data) return <p role="status" className="mt-1 text-xs text-ks-grey-1">{COPY.projPending(money(typed, currency))}</p>;
  const p = query.data;
  const cur = p.currency;
  const m = p.margins;
  const day = asOf !== null ? humanDate(p.as_of) : null;
  const onDate = asOf !== null && p.as_of === asOf;
  return (
    <section aria-label={COPY.projRegion} className="mt-2 rounded-md bg-ks-purple-4 p-2 text-xs text-ks-grey-1">
      <p className="text-sm text-ks-ink">
        <b>{COPY.projAt(money(p.price.value, cur))}{day ? AS_OF_COPY.boxOn(day) : ""}</b>: <span className={`tabular-nums ${grossTone(m.gross_pct, target)}`}>Gross {pct1(m.gross_pct)}</span>
        {belowTarget(m.gross_pct, target) && <> <Badge variant="outline" className="border-ks-warn text-ks-warn">{COPY.belowTarget}</Badge></>} · Fulfil {pct1(m.fulfilment_pct)} · Fees {pct1(m.fees_pct)}
      </p>
      <p>{p.current ? `${current} ${money(p.current.price, cur)}${day ? AS_OF_COPY.boxOn(day) : ""}: ${COPY.projMargins(pct1(p.current.margins.gross_pct), pct1(p.current.margins.fulfilment_pct), pct1(p.current.margins.fees_pct))}` : COPY.projNoCommitted}</p>
      {p.channel === "amazon" ? (
        <p>{[...(p.lines[0]?.components ?? []).map((c) => componentText(c, cur)), ...(p.fulfilment_total != null ? [COPY.projTotal(money(p.fulfilment_total, cur))] : [])].join(" · ")}</p>
      ) : (
        <>
          {p.lines.map((line) => (
            <p key={line.location ?? "?"} className={own !== null && line.location === own ? "font-bold" : undefined}>{countryName(line.location)}: {COPY.projMargins(pct1(line.margins.gross_pct), pct1(line.margins.fulfilment_pct), pct1(line.margins.fees_pct))} · {orders(line.orders)}</p>
          ))}
          <p>{projectionWeighting(p.weighting)}</p>
        </>
      )}
      <p>{metricsText(p, current)}</p>
      {p.missing.length > 0 && <p role="status">{COPY.projMissing(p.missing.map(projectionMissing).join(", "))}</p>}
      <p>{COPY.projFooter(BASIS_LABEL[p.cogs_basis], p.net_price == null ? null : money(p.net_price, cur))}</p>
      {onDate && day && <p className="text-ks-ink">{(p.changes ?? []).length > 0 ? AS_OF_COPY.boxChanges(day, (p.changes ?? []).map((c) => changeText(c, cur, true)).join("; ")) : AS_OF_COPY.boxNoChanges(day)}</p>}
      {onDate && day && p.strategy && <p>{AS_OF_COPY.boxRules(day)}</p>}
      {p.strategy && <StrategyLines s={p.strategy} currency={cur} onUse={onUse} />}
      <FeedIssuesNote issues={mergeIssues(p.feed_issues)} compact />
    </section>
  );
}

/**
 * The margins at the typed price on the date of the row's next fee change, under today's (C1 v1.28, c1-drawer-fee-change; owner
 * ruling A2 of review round 1): what moves, today's figure beside the date's, both C2's strings: Fulfil and Fees at the typed
 * price, then Gross once and plain (C2's Gross is net less COGS, and a date moves the fees only, so it never differs from today's
 * line and takes no second tone or badge), then the committed price's Fulfil and Fees. Shown once today's answer is on screen
 * and good (a refresh of it that failed shows the alert above alone, review round 2), so a refusal of the price is said once.
 * Pending: a status line. Failed: an alert (AGENTS rule 14). An answer that names another day shows nothing, and so does one
 * that lists no fee that differs and names no input missing that today's does not (C2's today is the date or later, so the date
 * is no longer ahead: C1 v1.25 D7, review rounds 1 and 2); C2 drops a fee it cannot convert without listing a change, so a
 * missing input alone shows the line, which names it as the line above names today's. The rules at that date are the picked
 * date's to show, not this line's.
 */
function FeeDayBlock({ current, day, today, query }: { current: string; day: string; today: Projection; query: UseQueryResult<Projection> }) {
  const date = humanDate(day);
  if (query.isError) return <p role="alert" className="mt-2 text-sm">{AS_OF_COPY.feeDayUnavailable(date)}</p>;
  if (!query.data) return <p role="status" className="mt-2 text-xs text-ks-grey-1">{AS_OF_COPY.feeDayPending(date)}</p>;
  const p = query.data;
  if (p.as_of !== day || ((p.changes ?? []).length === 0 && p.missing.every((m) => today.missing.includes(m)))) return null;
  const cur = p.currency;
  const m = p.margins;
  const t = today.margins;
  return (
    <section aria-label={AS_OF_COPY.feeDayLabel(date)} className="mt-2 rounded-md border border-ks-purple p-2 text-xs text-ks-grey-1">
      <p className="font-semibold text-ks-purple">{AS_OF_COPY.feeDayHead(date)}</p>
      <p className="text-sm text-ks-ink">
        <b>{COPY.projAt(money(p.price.value, cur))}</b>: {AS_OF_COPY.feeDayMoves(pct1(t.fulfilment_pct), pct1(m.fulfilment_pct), pct1(t.fees_pct), pct1(m.fees_pct))} · Gross {pct1(m.gross_pct)}
      </p>
      <p>{p.current && today.current
        ? `${current} ${money(p.current.price, cur)}: ${AS_OF_COPY.feeDayMoves(pct1(today.current.margins.fulfilment_pct), pct1(p.current.margins.fulfilment_pct), pct1(today.current.margins.fees_pct), pct1(p.current.margins.fees_pct))}`
        : COPY.projNoCommitted}</p>
      {p.missing.length > 0 && <p role="status">{COPY.projMissing(p.missing.map(projectionMissing).join(", "))}</p>}
    </section>
  );
}

/**
 * The assigned strategy at the typed price (C2 v1.14 `strategy` on the project answer; approved design M25 drawer 3003): the chip,
 * one line per rule, the goal, and the suggested price with a button that fills the box. Nothing is saved by the button (INV-4).
 */
export function StrategyLines({ s, currency, onUse }: { s: StrategyBlock; currency: string | null; onUse: (price: string) => void }) {
  if (s.not_strategised) return <p className="mt-1">{s.not_strategised}</p>;
  if (!s.assigned) return <p className="mt-1">{s.not_assigned ?? EVAL_COPY.noStrategy}</p>;
  const sp = s.suggested_price;
  return (
    <div aria-label={EVAL_COPY.region} className="mt-1 border-t border-ks-purple-3 pt-1">
      <p className="text-sm text-ks-ink"><b>{s.assigned.name}</b> {s.tone && s.summary && <Badge variant="outline" className={TONE_CLASS[s.tone]}>{s.summary}</Badge>}{!s.tone && s.summary && <span className="text-ks-grey-1">{s.summary}</span>}</p>
      {s.rules.map((rule) => (
        <p key={rule.rule_id}>{EVAL_COPY.rule(rule.priority)}: {rule.text} · <span className={rule.status === "met" ? "font-semibold text-ks-ink" : rule.status === "not_met" ? "font-semibold text-ks-warn" : undefined}>{STATUS_WORD[rule.status]}</span>{rule.value != null && ` · ${EVAL_COPY.now(rule.value, rule.currency)}`}{rule.status === "n_a" || rule.status === "no_data" ? ` (${rule.reason})` : ""}{rule.dropped && rule.dropped_reason ? ` · ${EVAL_COPY.dropped(rule.dropped_reason)}` : ""}</p>
      ))}
      {s.goal && <p>Goal: {s.goal.text}{s.goal.value != null && s.goal.target != null ? ` · ${EVAL_COPY.goalNow(s.goal.value, s.goal.target, s.goal.currency)}` : s.goal.reason ? ` (${s.goal.reason})` : ""}</p>}
      {sp && sp.value != null && sp.source ? (
        <p className="flex flex-wrap items-center gap-2 text-sm text-ks-ink">
          <b>{EVAL_COPY.suggested(money(sp.value, currency))}</b>
          <span className="text-xs text-ks-grey-1">{SOURCE_WORD[sp.source]}{sp.window ? ` · ${EVAL_COPY.window(money(sp.window.low, currency), sp.window.high == null ? null : money(sp.window.high, currency))}` : ""} · {sp.verified ? EVAL_COPY.verified : EVAL_COPY.unverified}</span>
          <Button type="button" size="sm" variant="outline" onClick={() => onUse(sp.value as string)}>{EVAL_COPY.use(money(sp.value, currency))}</Button>
        </p>
      ) : sp && sp.reason ? <p>{EVAL_COPY.noSuggestion(sp.reason)}</p> : null}
    </div>
  );
}
